import React, { useCallback, useRef, useState } from "react";
import { TZDate } from "@date-fns/tz";
import {
  IconDots,
  IconPencil,
  IconTrash,
} from "@tabler/icons-react";
import { queryClient } from "@shared/api/query-client";
import { useRemoveAppointmentsNoteModal } from "@features/appointment/remove-appointments-note/model/use-remove-appointments-note-modal";
import { useUpdateAppointmentNote } from "@widgets/appointment/api/useUpdateAppointmentNote";
import { getFullName } from "@utils/lib/getFullname";
import { useClickOutside } from "@utils/lib/useClickOutside";
import { AppointmentContentBlock } from "../appointment-content-block/appointment-content-block";
import { MaterialComposer } from "../material-composer/material-composer";
import s from "./appointment-note.module.scss";

const EditNote = ({ note, appointmentId, onSuccess }) => {
  const { mutateAsync: editNote } = useUpdateAppointmentNote(appointmentId);

  const onSave = useCallback(
    ({ text, attachments, existingFileIds }) =>
      editNote({
        appointmentId,
        noteId: note.id,
        data: { text: text || null },
        attachments,
        filesId: existingFileIds,
      }),
    [appointmentId, editNote, note?.id]
  );

  return (
    <MaterialComposer
      submitLabel="Сохранить"
      startExpanded
      initialText={note?.text || ""}
      initialAttachments={note?.attachments || []}
      onSubmit={onSave}
      onCancel={onSuccess}
      showCollapseButton={false}
    />
  );
};

const isSameId = (a, b) => String(a) === String(b);

const formatNoteDate = (dateString, timezone) => {
  if (!dateString) {
    return "";
  }

  const date = timezone ? new TZDate(dateString, timezone) : new Date(dateString);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${day}.${month}.${year} ${hours}:${minutes}`;
};

export const AppointmentNote = ({ note, appointmentId, showAuthorInfo = true }) => {
  const profileData = queryClient.getQueryData(["profile"]);
  const profile = profileData?.data || profileData;
  const currentUserId = profile?.id;

  const noteUserId = note?.user_id || note?.user?.id;
  const canManage = currentUserId && noteUserId && isSameId(currentUserId, noteUserId);

  const { open: openRemoveModal } = useRemoveAppointmentsNoteModal();
  const [isEditable, setIsEditable] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useClickOutside(menuRef, () => {
    if (isMenuOpen) {
      setTimeout(() => setIsMenuOpen(false), 50);
    }
  });

  const noteAuthor = note?.user || note || {};
  const fullName = getFullName(noteAuthor).trim();
  const authorName = fullName || noteAuthor?.username || "Пользователь";

  const avatarSrc = noteAuthor?.avatar?.image
    ? process.env.REACT_APP_STORAGE_URL + noteAuthor.avatar.image
    : process.env.REACT_APP_STORAGE_URL + "images/default_avatar.png";

  const noteDate = formatNoteDate(
    note?.created_at || note?.updated_at,
    profile?.timezone
  );

  if (isEditable && canManage) {
    return <EditNote {...{ note, appointmentId }} onSuccess={() => setIsEditable(false)} />;
  }

  return (
    <div className={s.noteCard}>
      <div className={s.header}>
        {showAuthorInfo ? (
          <div className={s.author}>
            <div className={s.avatar}>
              <img src={avatarSrc} alt={authorName} />
            </div>
            <div className={s.authorInfo}>
              <div className={s.authorName}>{authorName}</div>
              {noteDate ? <div className={s.date}>{noteDate}</div> : null}
            </div>
          </div>
        ) : (
          <div className={s.compactMeta}>{noteDate}</div>
        )}

        {canManage ? (
          <div className={s.menuWrapper} ref={menuRef}>
            <button
              className={s.menuButton}
              type="button"
              aria-label="Действия с заметкой"
              onClick={() => setIsMenuOpen((prev) => !prev)}
            >
              <IconDots size={20} />
            </button>

            {isMenuOpen ? (
              <div className={s.menu}>
                <button
                  className={s.menuItem}
                  type="button"
                  onClick={() => {
                    setIsMenuOpen(false);
                    setIsEditable(true);
                  }}
                >
                  <IconPencil size={18} />
                  Редактировать
                </button>
                <button
                  className={`${s.menuItem} ${s.dangerItem}`}
                  type="button"
                  onClick={() => {
                    setIsMenuOpen(false);
                    openRemoveModal({ appointmentId, noteId: note.id });
                  }}
                >
                  <IconTrash size={18} />
                  Удалить материал
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className={s.contentBlock}>
        <AppointmentContentBlock attachments={note?.attachments} text={note?.text} />
      </div>
    </div>
  );
};
